import React from 'react'
import styled from 'styled-components'

/* Numbered pagination for the order-history pages
   (CustomerOrder, DeliveryReadyOrder, DeliveredOrder, CanceledOrders).
   Long page lists are windowed (1 … 4 5 6 … 12) so the bar stays
   compact on mobile. */

const Bar = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0 0 18px;
`;

const PageNum = styled.button`
  min-width: 38px;
  height: 38px;
  padding: 0 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  border: 1px solid ${props => (props.$active ? '#01bf71' : 'rgba(31, 45, 61, 0.15)')};
  background: ${props => (props.$active ? '#01bf71' : '#fff')};
  color: ${props => (props.$active ? '#fff' : '#5a6b7a')};

  &:hover:not(:disabled) {
    border-color: #01bf71;
    color: ${props => (props.$active ? '#fff' : '#0a8f57')};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Arrow = styled(PageNum)`
  border: none;
  background: #01bf71;
  color: #fff;

  &:hover:not(:disabled) {
    background: #04a763;
    color: #fff;
  }
`;

const Dots = styled.span`
  min-width: 24px;
  text-align: center;
  color: #8a99a8;
  font-weight: 700;
  user-select: none;
`;

/* 1 and the last page always show; other pages only within one step of
   the current page, with a single ellipsis per collapsed gap */
const pageWindow = (page, pageCount) => {
  const items = []
  for (let p = 1; p <= pageCount; p++) {
    if (p === 1 || p === pageCount || Math.abs(p - page) <= 1) {
      items.push(p)
    } else if (items[items.length - 1] !== '…') {
      items.push('…')
    }
  }
  return items
}

const NumberPager = ({ page, pageCount, onPage, disabled }) => {
  if (!pageCount || pageCount < 2) return null

  return (
    <Bar aria-label="Order history pages">
      <Arrow
        type="button"
        aria-label="Previous page"
        disabled={disabled || page === 1}
        onClick={() => onPage(page - 1)}
      >
        ←
      </Arrow>

      {pageWindow(page, pageCount).map((p, i) =>
        p === '…' ? (
          <Dots key={`dots-${i}`}>…</Dots>
        ) : (
          <PageNum
            key={p}
            type="button"
            $active={p === page}
            aria-current={p === page ? 'page' : undefined}
            disabled={disabled}
            onClick={() => p !== page && onPage(p)}
          >
            {p}
          </PageNum>
        )
      )}

      <Arrow
        type="button"
        aria-label="Next page"
        disabled={disabled || page === pageCount}
        onClick={() => onPage(page + 1)}
      >
        →
      </Arrow>
    </Bar>
  )
}

export default NumberPager
